import { LogLevel, type LogEntry } from '../types';
import { logExporter } from './logExporter';
import { telemetry, TelemetryEvent } from './telemetry';

// Optional OpenTelemetry types for type safety
interface OtelLogger {
    emit: (params: any) => void;
}

export { LogLevel };
export type { LogEntry };

export class BaseLogger {
    protected logLevel: LogLevel = LogLevel.INFO;
    protected serviceName: string;

    constructor(serviceName: string) {
        this.serviceName = serviceName;
        // Optional: load log level from local storage if available
        if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
            const savedLevel = localStorage.getItem('LOG_LEVEL');
            if (savedLevel && LogLevel[savedLevel as keyof typeof LogLevel] !== undefined) {
                this.logLevel = LogLevel[savedLevel as keyof typeof LogLevel];
            }
        }
    }

    protected sanitize(data: any): any {
        try {
            return JSON.parse(JSON.stringify(data));
        } catch {
            return '[Circular/Unserializable]';
        }
    }

    protected formatEntry(level: LogLevel, message: string, context?: any, error?: unknown): LogEntry {
        const levelName = LogLevel[level];
        const timestamp = new Date().toISOString();

        const entry: LogEntry = {
            timestamp,
            level: levelName,
            message,
            context: context ? this.sanitize(context) : undefined,
        };

        if (error) {
            entry.error = error instanceof Error ? error.message : String(error);
            entry.stack = error instanceof Error ? error.stack : undefined;
        }

        return entry;
    }

    protected emitConsole(level: LogLevel, message: string, context?: any, error?: unknown) {
        if (level < this.logLevel) return;
        const entry = this.formatEntry(level, message, context, error);

        const consoleMethod = level === LogLevel.ERROR ? 'error' : level === LogLevel.WARN ? 'warn' : 'log';
        const prefix = `[${entry.timestamp}] [${this.serviceName}] [${entry.level}]`;

        if (context || error) {
            console[consoleMethod](prefix, message, context || '', error || '');
        } else {
            console[consoleMethod](prefix, message);
        }
    }

    public debug(message: string, contextOrError?: any) {
        if (contextOrError instanceof Error) this.emit(LogLevel.DEBUG, message, undefined, contextOrError);
        else this.emit(LogLevel.DEBUG, message, contextOrError);
    }

    public info(message: string, contextOrError?: any) {
        if (contextOrError instanceof Error) this.emit(LogLevel.INFO, message, undefined, contextOrError);
        else this.emit(LogLevel.INFO, message, contextOrError);
    }

    public warn(message: string, contextOrError?: any) {
        if (contextOrError instanceof Error) this.emit(LogLevel.WARN, message, undefined, contextOrError);
        else this.emit(LogLevel.WARN, message, contextOrError);
    }

    public error(message: string, errorOrContext?: any, context?: any) {
        if (errorOrContext instanceof Error) this.emit(LogLevel.ERROR, message, context, errorOrContext);
        else if (typeof errorOrContext === 'string') this.emit(LogLevel.ERROR, message, context, new Error(errorOrContext));
        else this.emit(LogLevel.ERROR, message, errorOrContext, undefined);
    }

    // Override in platform-specific loggers
    protected emit(level: LogLevel, message: string, context?: any, error?: unknown) {
        this.emitConsole(level, message, context, error);

        const entry = this.formatEntry(level, message, context, error);

        // Optional OpenTelemetry emission
        try {
            // This is a dynamic check for @opentelemetry/api-logs
            // In a real shared lib, we'd use a more robust way to handle optional deps
            if (typeof (globalThis as any)._OTEL_LOGGER !== 'undefined') {
                const otelLogger: OtelLogger = (globalThis as any)._OTEL_LOGGER;
                otelLogger.emit({
                    severityNumber: (level + 1) * 4, // Simple mapping
                    severityText: entry.level,
                    body: message,
                    attributes: {
                        service: this.serviceName,
                        ...entry.context,
                        ...(entry.error ? { error: entry.error, stack: entry.stack } : {})
                    }
                });
            }
        } catch { /* Ignore otel errors */ }

        // Export significant logs
        if (level >= LogLevel.WARN) {
            logExporter.enqueue(entry);
        }

        // Track errors in telemetry
        if (level === LogLevel.ERROR) {
            telemetry.track(TelemetryEvent.ERROR, {
                message,
                service: this.serviceName,
                error: entry.error,
                context: entry.context
            });
        }
    }

    public setLogLevel(level: LogLevel) {
        this.logLevel = level;
    }
}

export const Logger = new BaseLogger('Shared');
